import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useMetro } from '../context/MetroContext'

function Stations() {
  const { stations, lines, selectedCity, currentCity, isLoading } = useMetro()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedLine, setSelectedLine] = useState('all')

  const filteredStations = useMemo(() => {
    return stations.filter(station => {
      const matchesSearch = station.name.toLowerCase().includes(searchQuery.toLowerCase())
      const matchesLine = selectedLine === 'all' || station.lines.includes(selectedLine)
      return matchesSearch && matchesLine
    })
  }, [stations, searchQuery, selectedLine])

  if (!selectedCity) {
    return (
      <div className="py-8 lg:py-12">
        <div className="container-responsive">
          <div className="card text-center py-12">
            <span className="text-6xl mb-4 block">🏙️</span>
            <h2 className="text-xl font-semibold mb-2">No City Selected</h2>
            <p className="text-neutral-dark/70 mb-4">Please select a city to view stations</p>
            <Link to="/cities" className="btn-primary">
              Select City
            </Link>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="py-8 lg:py-12">
        <div className="container-responsive">
          <div className="card text-center py-12">
            <div className="animate-spin w-12 h-12 border-4 border-metro-blue border-t-transparent rounded-full mx-auto mb-4"></div>
            <p>Loading stations...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="py-8 lg:py-12">
      <div className="container-responsive">
        <h1 className="text-3xl lg:text-4xl 2xl:text-tv-2xl font-bold text-center mb-2">
          {currentCity?.metroName} Stations
        </h1>
        <p className="text-center text-neutral-dark/70 mb-8">
          {stations.length} stations across {lines.length} lines
        </p>

        {/* Search and Filter */}
        <div className="card mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search stations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-metro-blue focus:border-transparent"
              />
            </div>
            <select
              value={selectedLine}
              onChange={(e) => setSelectedLine(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-metro-blue focus:border-transparent"
            >
              <option value="all">All Lines</option>
              {lines.map(line => (
                <option key={line.id} value={line.id}>{line.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Station List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
          {filteredStations.map((station) => {
            const stationLines = station.lines
              .map(lineId => lines.find(l => l.id === lineId))
              .filter(Boolean)

            return (
              <Link
                key={station.id}
                to={`/station/${station.id}`}
                className="card hover:border-metro-blue transition-colors tv-focusable"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-lg">{station.name}</h3>
                  {station.isInterchange && (
                    <span className="px-2 py-1 bg-metro-violet-soft text-metro-violet text-xs font-medium rounded">
                      Interchange
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {stationLines.map(line => line && (
                    <span
                      key={line.id}
                      className="flex items-center gap-1 text-xs px-2 py-1 rounded-full"
                      style={{ backgroundColor: `${line.colorHex}20`, color: line.colorHex }}
                    >
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: line.colorHex }}
                      ></span>
                      {line.name}
                    </span>
                  ))}
                </div>
              </Link>
            )
          })}
        </div>

        {filteredStations.length === 0 && (
          <div className="card text-center py-12 text-neutral-dark/50">
            <span className="text-5xl mb-4 block">🔍</span>
            <p>No stations found matching your search</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default Stations
